import React, { Component } from "react";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Icon from "@material-ui/core/Icon";
import Tooltip from "@material-ui/core/Tooltip";
import Divider from "@material-ui/core/Divider";

import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";

import api from "api/index";

const APPEALABLE_FIELDS = [
  { key: "firstName", label: "First name", group: "Identity", inputType: "text" },
  { key: "lastName", label: "Last name", group: "Identity", inputType: "text" },
  { key: "dob", label: "Date of birth", group: "Identity", inputType: "date" },
  { key: "gender", label: "Gender", group: "Identity", inputType: "text" },
  { key: "nationality", label: "Nationality", group: "Identity", inputType: "text" },
  { key: "email", label: "Email", group: "Contact and guardian", inputType: "email" },
  { key: "phone", label: "Phone", group: "Contact and guardian", inputType: "tel" },
  { key: "address", label: "Address", group: "Contact and guardian", inputType: "text" },
  { key: "guardianName", label: "Guardian", group: "Contact and guardian", inputType: "text" },
  { key: "guardianPhone", label: "Guardian phone", group: "Contact and guardian", inputType: "tel" }
];

const FIELD_LABELS = APPEALABLE_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: f.label }), {});

const TYPE_HINT = {
  text: "Enter as plain text.",
  date: "Use the date picker or type as YYYY-MM-DD.",
  email: "Must be a valid email address.",
  tel: "Include the country code, e.g. +256700000000.",
  number: "Numbers only."
};

const STATUS_TONE = { pending: "warning", approved: "success", rejected: "danger" };

const STATUS_META = {
  active: { tone: "success", icon: "check_circle", label: "Verified" },
  deferred: { tone: "warning", icon: "schedule", label: "Deferred" },
  withdrawn: { tone: "danger", icon: "block", label: "Withdrawn" },
  completed: { tone: "info", icon: "task_alt", label: "Completed" }
};

const appealColumns = [
  { id: "field", header: "Field", accessorFn: a => FIELD_LABELS[a.field] || a.field },
  { id: "proposed", header: "Requested Value", accessorFn: a => a.proposedValue },
  {
    id: "status",
    header: "Status",
    cell: info => <Pill tone={STATUS_TONE[info.row.original.status] || "muted"}>{info.row.original.status}</Pill>
  },
  { id: "remark", header: "Admin Remark", accessorFn: a => a.adminRemark || "—" },
  { id: "date", header: "Submitted", accessorFn: a => (a.createdAt ? new Date(a.createdAt).toLocaleDateString() : "—") }
];

const styles = {
  page: { padding: "10px" },

  profileCard: {
    position: "relative",
    background: "#fff",
    borderRadius: "20px",
    padding: "24px 28px",
    marginBottom: "24px",
    boxShadow: "0 2px 12px 0 rgba(15, 23, 42, 0.04)",
    border: "1px solid #eef0f3"
  },
  profileTop: { display: "flex", alignItems: "center", gap: "16px" },
  avatar: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    background: "#dbeafe",
    color: "#1d4ed8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    fontWeight: 700,
    flexShrink: 0
  },
  profileName: { fontSize: "19px", fontWeight: 700, color: "#0f172a", margin: 0 },
  profileMeta: { fontSize: "13px", color: "#64748b", marginTop: "4px" },
  moreButton: { position: "absolute", top: "14px", right: "14px", color: "#94a3b8", minWidth: "auto", padding: "6px !important" },

  profileActions: { display: "flex", alignItems: "center", gap: "10px", marginTop: "18px" },

  panelsRow: { display: "flex", gap: "24px", flexWrap: "wrap", marginBottom: "24px" },
  panel: {
    flex: "1 1 320px",
    background: "#fff",
    borderRadius: "16px",
    padding: "22px 24px",
    boxShadow: "0 2px 12px 0 rgba(15, 23, 42, 0.04)",
    border: "1px solid #eef0f3"
  },
  panelTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "15px",
    fontWeight: 700,
    color: "#0f172a",
    margin: "0 0 16px"
  },
  panelIcon: { fontSize: "19px", color: "#475569" },

  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 0",
    "&:hover $rowEdit": { opacity: 1 }
  },
  rowLabel: { fontSize: "14px", color: "#64748b" },
  rowValueWrap: { display: "flex", alignItems: "center", gap: "4px" },
  rowValue: { fontSize: "14px", color: "#0f172a", fontWeight: 600, textAlign: "right" },
  rowEdit: { opacity: 0.3, transition: "opacity 0.15s", padding: "4px !important", minWidth: "auto" },

  sectionTitle: {
    color: "#1e293b",
    fontWeight: 700,
    fontSize: "15px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "18px"
  },
  sectionIcon: { fontSize: "19px", color: "#475569" },

  appealsCard: {
    padding: "8px 24px 16px",
    borderRadius: "16px",
    background: "#fff",
    boxShadow: "0 2px 12px 0 rgba(15, 23, 42, 0.04)",
    border: "1px solid #eef0f3"
  },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  dialogLabel: { fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" },
  fieldSelect: {
    width: "100%",
    padding: "10px 14px",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    fontSize: "14px",
    marginBottom: "16px",
    background: "#f8fafc",
    fontWeight: 600,
    color: "#1e293b",
    outline: "none"
  }
};

class PortalBiodata extends Component {
  state = {
    profile: null, appeals: [], loading: true, refreshing: false,
    dialogOpen: false, dialogField: null,
    form: { proposedValue: "", reason: "" }, saving: false, error: null
  };

  componentDidMount() {
    this.load();
  }

  load = async () => {
    this.setState({ refreshing: true });
    try {
      const [profileRes, appealsRes] = await Promise.all([
        api.get("/portal/profile"),
        api.get("/portal/appeals")
      ]);
      const profile = profileRes.data.data || profileRes.data;
      const appeals = appealsRes.data.data || appealsRes.data;
      this.setState({ profile, appeals: Array.isArray(appeals) ? appeals : [], loading: false, refreshing: false });
    } catch (e) {
      this.setState({ loading: false, refreshing: false });
    }
  };

  openAppealDialog = (field) => {
    this.setState({
      dialogOpen: true,
      dialogField: field,
      form: { proposedValue: "", reason: "" },
      error: null
    });
  };

  handleDialogFieldChange = e => {
    const field = APPEALABLE_FIELDS.find(f => f.key === e.target.value) || APPEALABLE_FIELDS[0];
    this.setState({ dialogField: field, form: { proposedValue: "", reason: "" }, error: null });
  };

  handleFormChange = key => e => {
    const v = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [key]: v } }));
  };

  handleSubmitAppeal = async () => {
    const { dialogField, form } = this.state;
    if (!form.proposedValue.trim()) {
      this.setState({ error: "Please enter the value you're requesting." });
      return;
    }
    this.setState({ saving: true, error: null });
    try {
      await api.post("/portal/appeals", {
        field: dialogField.key,
        proposedValue: form.proposedValue.trim(),
        reason: form.reason.trim()
      });
      this.setState({ dialogOpen: false, saving: false });
      this.load();
    } catch (e) {
      this.setState({
        error: e.response?.data?.message || "Failed to submit correction request.",
        saving: false
      });
    }
  };

  Row = (field) => {
    const { classes } = this.props;
    const { profile } = this.state;
    const value = profile ? profile[field.key] : null;
    return (
      <div className={classes.row} key={field.key}>
        <span className={classes.rowLabel}>{field.label}</span>
        <div className={classes.rowValueWrap}>
          <span className={classes.rowValue}>{value || "—"}</span>
          <Tooltip title={`Request a correction for ${field.label}`}>
            <Button justIcon size="sm" simple color="info" className={classes.rowEdit} onClick={() => this.openAppealDialog(field)}>
              <Icon style={{ fontSize: "15px" }}>edit</Icon>
            </Button>
          </Tooltip>
        </div>
      </div>
    );
  };

  render() {
    const { classes } = this.props;
    const { profile, appeals, loading, refreshing, dialogOpen, dialogField, form, saving, error } = this.state;
    const { Row } = this;

    if (loading) return <div style={{ textAlign: "center", padding: "100px" }}><CircularProgress /></div>;

    const identityFields = APPEALABLE_FIELDS.filter(f => f.group === "Identity");
    const contactFields = APPEALABLE_FIELDS.filter(f => f.group === "Contact and guardian");
    const initials = `${(profile?.firstName || "")[0] || ""}${(profile?.lastName || "")[0] || ""}`.toUpperCase();
    const statusMeta = STATUS_META[profile?.status] || { tone: "muted", icon: "help", label: profile?.status || "Unknown" };

    return (
      <div className={classes.page}>
        <div className={classes.profileCard}>
          <Tooltip title="Refresh">
            <Button justIcon simple className={classes.moreButton} onClick={this.load} disabled={refreshing}>
              {refreshing ? <CircularProgress size={16} /> : <Icon>more_horiz</Icon>}
            </Button>
          </Tooltip>

          <div className={classes.profileTop}>
            <div className={classes.avatar}>{initials || "?"}</div>
            <div>
              <h3 className={classes.profileName}>{profile?.firstName} {profile?.lastName}</h3>
              <div className={classes.profileMeta}>
                {[profile?.studentNo, profile?.programme?.name, profile?.yearOfStudy ? `Year ${profile.yearOfStudy}` : null]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
          </div>

          <div className={classes.profileActions}>
            <Pill tone={statusMeta.tone}>
              <Icon style={{ fontSize: "14px", verticalAlign: "middle", marginRight: "4px" }}>{statusMeta.icon}</Icon>
              {statusMeta.label}
            </Pill>
            <Button outlined color="info" size="sm" onClick={() => this.openAppealDialog(APPEALABLE_FIELDS[0])}>
              <Icon style={{ marginRight: "6px", fontSize: "18px" }}>flag</Icon> Request correction
            </Button>
          </div>
        </div>

        <div className={classes.panelsRow}>
          <div className={classes.panel}>
            <h4 className={classes.panelTitle}><Icon className={classes.panelIcon}>badge</Icon> Identity</h4>
            {identityFields.map(f => Row(f))}
          </div>
          <div className={classes.panel}>
            <h4 className={classes.panelTitle}><Icon className={classes.panelIcon}>contact_mail</Icon> Contact and guardian</h4>
            {contactFields.map(f => Row(f))}
          </div>
        </div>

        <div className={classes.appealsCard}>
          <h4 className={classes.sectionTitle} style={{ paddingTop: "16px" }}>
            <Icon className={classes.sectionIcon}>history_edu</Icon> My Correction Requests
          </h4>
          <DataTable
            columns={appealColumns}
            data={appeals}
            emptyState={
              <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                <Icon style={{ fontSize: "40px", opacity: 0.2 }}>fact_check</Icon>
                <p>You haven't requested any biodata corrections yet. Use "Request correction" above, or the edit icon next to a field, to submit one.</p>
              </div>
            }
          />
        </div>

        <Dialog open={dialogOpen} onClose={() => this.setState({ dialogOpen: false })} maxWidth="xs" fullWidth>
          <DialogHeader
            title="Request a Correction"
            subtitle="Submit the value you'd like your record updated to."
            icon="flag"
            color="info"
            onClose={() => this.setState({ dialogOpen: false })}
          />
          <DialogContent style={{ paddingTop: "20px" }}>
            {error && <p className={classes.errorMsg} style={{ marginBottom: "15px" }}>{error}</p>}
            <select className={classes.fieldSelect} value={dialogField?.key || ""} onChange={this.handleDialogFieldChange}>
              <optgroup label="Identity">
                {identityFields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </optgroup>
              <optgroup label="Contact and guardian">
                {contactFields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </optgroup>
            </select>
            <div className={classes.dialogLabel}>Current Value</div>
            <div className={classes.rowValue} style={{ fontSize: "14px", textAlign: "left", marginTop: "4px", marginBottom: "16px" }}>
              {dialogField && profile ? (profile[dialogField.key] || "—") : "—"}
            </div>
            <Divider style={{ marginBottom: "16px" }} />
            <CustomInput
              labelText="Requested Value"
              id="appealProposedValue"
              formControlProps={{ fullWidth: true }}
              labelProps={dialogField?.inputType === "date" ? { shrink: true } : undefined}
              inputProps={{
                value: form.proposedValue,
                onChange: this.handleFormChange("proposedValue"),
                type: dialogField?.inputType || "text"
              }}
              helpText={TYPE_HINT[dialogField?.inputType] || undefined}
            />
            <CustomInput
              labelText="Reason (optional)"
              id="appealReason"
              formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.reason, onChange: this.handleFormChange("reason"), multiline: true, rows: 3 }}
            />
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ dialogOpen: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSubmitAppeal} disabled={saving} style={{ fontWeight: "700" }}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Submit Request"}
            </Button>
          </DialogFooter>
        </Dialog>
      </div>
    );
  }
}

PortalBiodata.propTypes = { classes: PropTypes.object.isRequired };
export default withStyles(styles)(PortalBiodata);
